import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/data";

export default function AdminSubmission() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("life101_admin_token")) {
      nav("/admin/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/admin/submissions/${id}`);
        setData(data);
      } catch (e) {
        setErr("Kayıt yüklenemedi.");
      }
    })();
  }, [id, nav]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-300" style={{ background: "#050505" }}>{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-neutral-500" style={{ background: "#050505" }}>Yükleniyor...</div>;

  const { submission: s, lead } = data;
  const radarData = CATEGORIES.map((c) => ({ subject: c.label, value: s.scores[c.key] ?? 0 }));

  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="admin-submission-page">
      <header className="max-w-7xl mx-auto px-6 py-7 flex items-center justify-between">
        <Link to="/admin" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={16} /> Geri</Link>
        <div className="text-sm text-neutral-500">Kullanıcı Detayı</div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pb-16 space-y-6">
        <div className="glass-solid p-7">
          <h1 className="text-3xl font-semibold text-white">{lead?.first_name} {lead?.last_name}</h1>
          <p className="text-neutral-400 text-sm mt-1">{lead?.email}</p>
          <p className="text-neutral-600 text-xs mt-2">Test tarihi: {new Date(s.completed_at).toLocaleString("tr-TR")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass-solid p-7">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Genel Skor</div>
            <div className="mt-3 text-6xl font-bold h-gradient">{s.general_score}<span className="text-xl text-neutral-500 align-top ml-1">/100</span></div>
          </div>
          <div className="lg:col-span-2 glass-solid p-4">
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius="78%">
                <PolarGrid stroke="#1f1f1f" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "#a3a3a3", fontSize: 11 }} />
                <Radar dataKey="value" stroke="#ffffff" fill="rgba(255,255,255,0.15)" strokeWidth={1.5} dot={{ fill: "#fff", r: 3 }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-solid p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Güçlü Alanlar</div>
            <div className="flex flex-wrap gap-2">
              {s.strongest_areas.map((x) => <span key={x} className="px-3 py-1 rounded-full border border-white/15 text-sm">{x}</span>)}
            </div>
          </div>
          <div className="glass-solid p-6">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Gelişim Alanları</div>
            <div className="flex flex-wrap gap-2">
              {s.development_areas.map((x) => <span key={x} className="px-3 py-1 rounded-full border border-white/15 text-sm">{x}</span>)}
            </div>
          </div>
        </div>

        <div className="glass-solid p-6">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-4">Önerilen Kitaplar</div>
          <div className="flex flex-wrap gap-2">
            {(s.recommended_books || []).map((b, i) => (
              <span key={i} className="px-3 py-1 rounded-full border border-white/15 text-sm">{b.title || b.key}</span>
            ))}
          </div>
        </div>

        <div className="glass-solid overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-[0.12em]">
                <th className="p-4">#</th>
                <th className="p-4">Kategori</th>
                <th className="p-4">Ölçü</th>
                <th className="p-4">Soru</th>
                <th className="p-4">Cevap Değeri</th>
                <th className="p-4">Cevap Etiketi</th>
              </tr>
            </thead>
            <tbody>
              {(s.answers || []).map((a) => (
                <tr key={a.questionId} className="border-t border-white/5 align-top">
                  <td className="p-4 text-neutral-500">{a.questionId}</td>
                  <td className="p-4 text-neutral-200">{a.categoryLabel}</td>
                  <td className="p-4 text-neutral-400">{a.measure}</td>
                  <td className="p-4 text-neutral-300 max-w-md">{a.questionText}</td>
                  <td className="p-4 text-neutral-100">{a.answerValue}</td>
                  <td className="p-4 text-neutral-400">{a.answerLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
